import React, { useEffect, useState, useContext, useMemo } from 'react'
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Divider,
  Button,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../../App'
import API from '../../../services/API'
import TipTapReader from '../../../components/TipTap/TipTapReader'
import { Delete, Download, Edit } from '@mui/icons-material'
import NotFound from '../../../components/NotFound'
import BigModal from '../../../components/Modals/BigModal'
import PDFViewer from '../../../components/Modals/PDFViewer'

const CrewVaultSingle = () => {
  const { id } = useParams()
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const [vaultItem, setVaultItem] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [viewPDF, setViewPDF] = useState(null)
  const [openConfirmArchive, setOpenConfirmArchive] = useState(false)
  const navigate = useNavigate()

  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const {
    title,
    tags,
    roles,
    teams,
    content,
    createdAt,
    createdBy,
    updatedAt,
    updatedBy,
    attachments,
  } = vaultItem

  const allTeams = useMemo(
    () => [
      ...(orgSettings?.teams ?? []),
      ...(orgSettings?.additionalTeams ?? []),
    ],
    [orgSettings],
  )
  const allRoles = useMemo(() => [...(orgSettings?.roles ?? [])], [orgSettings])

  const normalise = (arr = []) =>
    [...new Set(arr.map((value) => String(value).trim().toLowerCase()))].sort()

  const arraysMatch = (a = [], b = []) => {
    const aNorm = normalise(a)
    const bNorm = normalise(b)

    if (aNorm.length !== bNorm.length) return false
    return aNorm.every((value, index) => value === bNorm[index])
  }

  const showAllRoles = roles?.length > 0 && arraysMatch(roles, allRoles)
  const showAllTeams = teams?.length > 0 && arraysMatch(teams, allTeams)

  useEffect(() => {
    API.get(`/crewvault/${id}`)
      .then((res) => {
        if (res.status === 200) {
          setVaultItem(res.data)
        } else setError(true)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <CircularProgress />
  if (error || !vaultItem) return <NotFound />

  const allowedToEdit =
    user?.security?.isSiteAdmin ||
    user?.employee?.teams?.includes('Manager') ||
    user?.employee?.teams?.includes('Governance')

  const handleDownload = (attachment) => {
    const fileType = attachment?.fileName?.split('.').pop()?.toLowerCase()

    if (fileType === 'pdf') {
      setViewPDF(attachment)
    } else {
      API.post('/gets3downloadurl', {
        key: attachment.s3key,
      }).then((res) => window.open(res.data, '_blank'))
    }
  }

  const handleConfirmArchive = () => {
    API.delete(`/crewvault/${id}`).then((res) => {
      if (res.status === 200) {
        navigate('/crewvault')
        setToast({ message: 'Crew Vault Archived' })
      }
    })
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        alignItems="space-between"
        justifyContent="space-between"
      >
        <Typography
          variant="h4"
          fontWeight={600}
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}
        >
          {title}
        </Typography>
        {allowedToEdit && (
          <Stack direction='row' alignItems='center' gap={1}>
            <Button
              size="small"
              color="error"
              startIcon={<Delete />}
              variant="contained"
              onClick={() => setOpenConfirmArchive(true)}
            >
              Delete
            </Button>
            <Button
              size="small"
              startIcon={<Edit />}
              variant="contained"
              onClick={() => navigate('/crewvault/edit/' + vaultItem._id)}
            >
              Edit
            </Button>
          </Stack>
        )}
      </Stack>

      <Paper elevation={0} sx={{ p: 1, backgroundColor: '#fff' }}>
        <TipTapReader content={content} />
      </Paper>

      <Paper elevation={0} sx={{ p: 1, mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="title3" color="anBlue">
              Attachments
            </Typography>
          </Grid>
          {attachments.map((attachment, index) => (
            <Grid item xs={12} lg={4} xl={3} key={attachment._id}>
              <Button
                size="small"
                startIcon={<Download />}
                color="anBlue"
                variant="outlined"
                onClick={() => handleDownload(attachment)}
                sx={{ maxWidth: '100%', wordBreak: 'break-all', textAlign: 'left' }}
              >
                {attachment.fileName}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {tags.map((tag, i) => (
            <Chip
              key={i}
              label={tag}
              size="small"
              color="anPurple"
              sx={{ borderRadius: 1 }}
            />
          ))}
          {showAllRoles ? (
            <Chip
              label="All Roles"
              size="small"
              sx={{ borderRadius: 1 }}
              color="primary"
              variant="outlined"
            />
          ) : (
            roles.map((role, i) => (
              <Chip
                key={`r-${i}`}
                label={role}
                size="small"
                sx={{ borderRadius: 1 }}
                color="primary"
              />
            ))
          )}
          {teams?.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {showAllTeams ? (
                <Chip
                  label="All Teams"
                  size="small"
                  sx={{ borderRadius: 1 }}
                  color="contrast"
                  variant="outlined"
                />
              ) : (
                teams.map((team, i) => (
                  <Chip
                    variant="outlined"
                    size="small"
                    label={team}
                    key={`t-${i}`}
                    sx={{ borderRadius: 1 }}
                    color="contrast"
                  />
                ))
              )}
            </Stack>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary">
          {createdBy
            ? `Created ${new Date(createdAt).toLocaleDateString()} by ${
                createdBy?.firstName
              } ${createdBy?.lastName}`
            : updatedBy
              ? `Updated ${new Date(updatedAt).toLocaleDateString()} by ${
                  updatedBy?.firstName
                } ${updatedBy?.lastName}`
              : ''}
        </Typography>
      </Paper>
      <PDFViewer
        open={!!viewPDF}
        onClose={() => setViewPDF(null)}
        s3key={viewPDF?.s3key}
        title={viewPDF?.fileName}
      />
      <Dialog
        open={openConfirmArchive}
        onClose={() => setOpenConfirmArchive(false)}
      >
        <DialogTitle>
          Are you sure you want to Archive this Crew Vault post?
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => setOpenConfirmArchive(false)}
            variant="outlined"
          >
            No - Cancel
          </Button>
          <Button
            onClick={() => handleConfirmArchive()}
            variant="contained"
            startIcon={<Delete />}
          >
            Yes - Archive
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CrewVaultSingle
